import { AxiosRequestConfig } from 'axios'

import { ApiRequest } from '../baseService'

/**
 * Activity History read API (backend: ActivityHistoryController, ADMIN_STAFF / STAFF only).
 * Every call sets trackActivity: false so opening the history never becomes part of a tracked
 * action (the interceptor also excludes app/activity-history, this keeps it explicit).
 */
const BaseRoute = 'app/activity-history'

const untracked: AxiosRequestConfig = { trackActivity: false }

/** ActivitySource (backend enum, serialised by name). */
export type ActivitySource =
  | 'UiAction'
  | 'Request'
  | 'BackgroundJob'
  | 'RecurringJob'

export type ActivityOperationKind = 'HttpRequest' | 'BackgroundJob'

export type ActivityChangeType = 'Created' | 'Updated' | 'Deleted'

export type LinkedChangeKind = 'Action' | 'External'

export type AttachmentChangeKind = 'Added' | 'Removed' | 'TypeChanged'

export interface ActivityLinkedChangeDto {
  /** CLIENT, CASE_MANAGER, SPECIALIST, COMPANY. */
  type: string
  id: string
  label?: string | null
  kind: LinkedChangeKind
  /** ISO-8601 UTC. */
  lastModificationTime?: string | null
  lastModifierName?: string | null
}

export interface ActivityActionListItemDto {
  id: string
  sequence: number
  clientActionId?: string | null
  actionName: string
  source: ActivitySource
  userId?: string | null
  userName?: string | null
  startedAt: string
  lastActivityAt: string
  statusBefore?: string | null
  statusAfter?: string | null
  sections: string[]
  requestCount: number
  jobCount: number
  /** Number of changed fields. */
  changeCount: number
  emailCount: number
  emailFailedCount: number
  attachmentsAdded: number
  attachmentsRemoved: number
  linkedChanges: ActivityLinkedChangeDto[]
}

export interface ActivityPropertyChangeDto {
  name: string
  original?: string | null
  new?: string | null
}

export interface ActivityEntityChangeDto {
  id: string
  entityType: string
  entityId: string
  entityLabel?: string | null
  sectionKey: string
  changeType: ActivityChangeType
  isShared: boolean
  properties: ActivityPropertyChangeDto[]
}

export interface ActivityOperationDto {
  id: string
  kind: ActivityOperationKind
  name: string
  httpMethod?: string | null
  path?: string | null
  statusCode?: number | null
  durationMs: number
  executedAt: string
  changes: ActivityEntityChangeDto[]
}

export interface ActivityEmailDto {
  id: string
  emailTrackingId: string
  activityActionId: string
  sequence: number
  actionName: string
  actionSource: ActivitySource
  userName?: string | null
  statusAtTrigger?: string | null
  templateKey: string
  destination: string
  triggeredAt: string
  queuedAt: string
  sentTime?: string | null
  /** EmailTrackingStatusEnum name: Pending, Successful, Error. */
  status: string
  /** SendGridEmailDeliveryStatus name: Accepted, Processed, Dropped, Deferred, Bounced, Delivered, Error. */
  deliveryStatus?: string | null
  lastOpenTime?: string | null
  errorMessage?: string | null
  sentByJob?: string | null
}

export interface ActivityAttachmentDto {
  kind: AttachmentChangeKind
  attachmentId: string
  fileName: string
  typeName?: string | null
  sizeInBytes?: number | null
  numberOfPages?: number | null
  where: string
  fileAvailable: boolean
}

export interface ActivityActionDetailDto {
  action: ActivityActionListItemDto
  operations: ActivityOperationDto[]
  emails: ActivityEmailDto[]
  attachments: ActivityAttachmentDto[]
}

export interface ActivityStatePropertyDto {
  name: string
  value?: string | null
  currentValue?: string | null
}

export interface ActivityStateEntityDto {
  entityType: string
  entityId: string
  entityLabel?: string | null
  isShared: boolean
  properties: ActivityStatePropertyDto[]
}

export interface ActivityStateDto {
  sequence: number
  sections: { sectionKey: string; entities: ActivityStateEntityDto[] }[]
}

export type ActivityCompareKind = 'Added' | 'Removed' | 'Changed'

export interface ActivityCompareEntityDto {
  entityType: string
  entityId: string
  entityLabel?: string | null
  kind: ActivityCompareKind
  properties: { name: string; a?: string | null; b?: string | null }[]
}

export interface ActivityCompareDto {
  from: number
  to: number
  sections: { sectionKey: string; entities: ActivityCompareEntityDto[] }[]
}

export interface ActivityActionsQuery {
  skipCount?: number
  maxResultCount?: number
  userId?: string
  /** User ids, comma-separated: actions of any of them. */
  userIds?: string
  /** With userIds: also the actions without a user (System); alone: only those. */
  includeSystem?: boolean
  /** ActivitySource names, comma-separated. */
  source?: string
  /** Section keys, comma-separated: actions that touched any of them. */
  section?: string
  actionName?: string
  withEmailsOnly?: boolean
  withAttachmentsOnly?: boolean
}

const subjectRoute = (subjectType: string, subjectId: string) =>
  `${BaseRoute}/${encodeURIComponent(subjectType)}/${encodeURIComponent(
    subjectId
  )}`

const GetActions = (
  subjectType: string,
  subjectId: string,
  params: ActivityActionsQuery
) =>
  ApiRequest.get<{ items: ActivityActionListItemDto[]; totalCount: number }>(
    `${subjectRoute(subjectType, subjectId)}/actions`,
    { ...untracked, params }
  )

const GetAction = (actionId: string) =>
  ApiRequest.get<ActivityActionDetailDto>(
    `${BaseRoute}/actions/${encodeURIComponent(actionId)}`,
    untracked
  )

const GetState = (subjectType: string, subjectId: string, sequence: number) =>
  ApiRequest.get<ActivityStateDto>(
    `${subjectRoute(subjectType, subjectId)}/state`,
    { ...untracked, params: { sequence } }
  )

const Compare = (
  subjectType: string,
  subjectId: string,
  from: number,
  to: number
) =>
  ApiRequest.get<ActivityCompareDto>(
    `${subjectRoute(subjectType, subjectId)}/compare`,
    { ...untracked, params: { from, to } }
  )

const GetEmails = (subjectType: string, subjectId: string) =>
  ApiRequest.get<{ items: ActivityEmailDto[] }>(
    `${subjectRoute(subjectType, subjectId)}/emails`,
    untracked
  )

const API = {
  GetActions,
  GetAction,
  GetState,
  Compare,
  GetEmails,
}

export default API
